'use client';

import { useId } from '@react-aria/utils';
import clsx from 'clsx';
import { useContext, type ForwardedRef } from 'react';
import {
  LabelContext as RACLabelContext,
  NumberField as RACNumberField,
  Provider as RACProvider,
  Slider as RACSlider,
  SliderOutput as RACSliderOutput,
  SliderStateContext as RACSliderStateContext,
  SliderThumb as RACSliderThumb,
  SliderTrack as RACSliderTrack,
  TextContext as RACTextContext,
  useSlottedContext,
  type NumberFieldProps as RACNumberFieldProps,
  type SliderProps as RACSliderProps,
} from 'react-aria-components';
import { forwardRef } from '../../forward-ref.js';
import { useProductionWarning } from '../../utils/use-production-warning.js';
import { withStyleProps } from '../../with-style-props.js';
import { FormFieldDescription, FormFieldLabel } from '../internal/form-field/form-field.js';
import { Input } from '../internal/input/input.js';
import type { CommonProps, LabellableProps } from '../types.js';
import { Text } from '../typography/text.js';

// TODO: move to translations when stabilized
const sliderStrings = {
  srMinimum: 'minimum',
  srMaximum: 'maximum',
};

export interface UNSTABLE_SliderProps<T extends number | number[] = number>
  extends
    CommonProps,
    LabellableProps,
    Pick<RACSliderProps<T>, 'isDisabled' | 'minValue' | 'maxValue' | 'step' | 'formatOptions'> {
  /** A description for the field. Provides a hint such as specific requirements for what to choose. */
  description?: string;
  /** The current value (controlled). */
  value?: T;
  /** The default value (uncontrolled). */
  defaultValue?: T;
  /** Handler that is called when the value changes. */
  onChange?: (value: T) => void;
  /** Handler that is called when the slider stops moving. */
  onChangeEnd?: (value: T) => void;
  /**
   * The `<form>` element to associate the input with.
   * The value of this attribute must be the id of a `<form>` in the same document.
   * See [MDN](https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/input#form).
   */
  form?: string;
  /**
   * The name of the input element, used when submitting an HTML form. See [MDN](https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/input#name).
   */
  name?: T extends number[] ? string[] : string;
  /** Show input fields for slider values. */
  withInput?: boolean;
  /** Show static labels for minimum and maximum values. */
  showLabels?: boolean;
}

function UNSTABLE_Slider<T extends number | number[] = number>(
  {
    label,
    description,
    UNSAFE_className,
    UNSAFE_style,
    form,
    name,
    withInput,
    showLabels,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    ...props
  }: UNSTABLE_SliderProps<T>,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const descriptionId = useId();

  useProductionWarning(() => {
    if (!label && !ariaLabel && !ariaLabelledBy) {
      console.warn('Slider requires one of label / aria-label / aria-labelledby for accessibility');
    }
  }, [label, ariaLabel, ariaLabelledBy]);

  return (
    <RACProvider values={[[RACTextContext, { slots: { description: { id: descriptionId } } }]]}>
      <RACSlider
        {...props}
        ref={ref}
        className={clsx('cim-slider', UNSAFE_className)}
        style={UNSAFE_style}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        orientation="horizontal"
        aria-describedby={[description && descriptionId, props['aria-describedby']].filter(Boolean).join(' ')}
      >
        {({ state, isDisabled }) => {
          return (
            <>
              <FormFieldLabel isRequired={false} isDisabled={isDisabled}>
                {label}
              </FormFieldLabel>
              {!withInput && label != null && (
                <RACSliderOutput className="cim-slider-output">
                  <Text as="span" variant="medium">
                    {state.values.map((_, i) => state.getThumbValueLabel(i)).join(' – ')}
                  </Text>
                </RACSliderOutput>
              )}
              <div className="cim-slider-track-container">
                {showLabels && (
                  <Text as="span" variant="medium" UNSAFE_className="cim-slider-label-min">
                    {state.getThumbMinValue(0)}
                  </Text>
                )}

                <RACSliderTrack className="cim-slider-track">
                  {({ state: trackState, isDisabled: trackIsDisabled }) => (
                    <>
                      <div className="cim-slider-track-background" data-disabled={trackIsDisabled || undefined} />
                      <div
                        className="cim-slider-fill"
                        style={
                          (state.values.length === 1
                            ? {
                                '--cim-slider-fill-size': trackState.getThumbPercent(0) * 100 + '%',
                              }
                            : state.values.length === 2
                              ? {
                                  '--cim-slider-fill-start': trackState.getThumbPercent(0) * 100 + '%',
                                  '--cim-slider-fill-size':
                                    (trackState.getThumbPercent(1) - trackState.getThumbPercent(0)) * 100 + '%',
                                }
                              : {}) as React.CSSProperties
                        }
                      />
                      {trackState.values.map((_, i) => (
                        <SliderThumb key={i} index={i} name={typeof name === 'string' ? name : name?.[i]} form={form} />
                      ))}
                    </>
                  )}
                </RACSliderTrack>
                {showLabels && (
                  <Text as="span" variant="medium" UNSAFE_className="cim-slider-label-max">
                    {state.getThumbMaxValue(state.values.length - 1)}
                  </Text>
                )}
                {withInput && (
                  <div className="cim-slider-inputs-container-wide">
                    {state.values.map((_, i) => (
                      <SliderNumberField
                        key={i}
                        index={i}
                        style={{ order: state.values.length === 2 ? (i === 0 ? -1 : 1) : undefined }}
                        formatOptions={props.formatOptions}
                      >
                        <Input width="fit-content" />
                      </SliderNumberField>
                    ))}
                  </div>
                )}
              </div>
              {withInput && (
                <div className="cim-slider-inputs-container-narrow">
                  {state.values.map((_, i) => (
                    <SliderNumberField key={i} index={i} formatOptions={props.formatOptions}>
                      <Input />
                    </SliderNumberField>
                  ))}
                </div>
              )}
              <FormFieldDescription>{description}</FormFieldDescription>
            </>
          );
        }}
      </RACSlider>
    </RACProvider>
  );
}

interface SliderNumberFieldProps extends RACNumberFieldProps {
  index: number;
}

function SliderNumberField({ index, ...props }: SliderNumberFieldProps) {
  const state = useContext(RACSliderStateContext)!;
  const labelProps = useSlottedContext(RACLabelContext)!;

  return (
    <RACNumberField
      {...props}
      className="cim-slider-input"
      value={state.values[index]}
      onChange={(value) => state.setThumbValue(index, value ?? 0)}
      aria-labelledby={labelProps.id}
      aria-label={
        state.values.length === 2 ? (index === 0 ? sliderStrings.srMinimum : sliderStrings.srMaximum) : undefined
      }
    />
  );
}

function SliderThumb({ index, name, form }: { index: number; name?: string | string[]; form?: string }) {
  const state = useContext(RACSliderStateContext)!;

  return (
    <RACSliderThumb
      index={index}
      className="cim-slider-thumb"
      name={typeof name === 'string' ? name : name?.[index]}
      form={form}
      aria-label={
        state.values.length === 2 ? (index === 0 ? sliderStrings.srMinimum : sliderStrings.srMaximum) : undefined
      }
    />
  );
}

/**
 * Allows users to select one or more values within a range.
 */
const _UNSTABLE_Slider = withStyleProps(forwardRef(UNSTABLE_Slider), 'Slider');

export { _UNSTABLE_Slider as UNSTABLE_Slider };
